import type { Json } from '@/types/database.generated';
import { PERSON_RELATION_TYPES, type PersonRelationType } from '@/lib/patientRelations';

/** Cave + Anamnesis + Examination + ActualMedicine. The builder uses `>`. */
export const CLINICAL_INFO_MAX_TOTAL = 15_750;

/**
 * XREF02 (radiology) has no combined budget. Each clinical field has its own
 * ceiling (XREF02.pdf p. 28): Cave tx..350, Anamnesis tx..3780, Examination
 * tx..1050, ActualMedicine tx..1050, FirstDateOfPeriod tx..70. They add up to
 * 6300, which is why a combined 6300 counter used to let Cave reach 500 and the
 * builder then refused the referral.
 */
export const XREF02_FIELD_MAX = {
  cave: 350,
  anamnesis: 3_780,
  examination: 1_050,
  actualMedicine: 1_050,
  firstDateOfPeriod: 70,
} as const;

export type Xref02ClinicalField = Exclude<keyof typeof XREF02_FIELD_MAX, 'firstDateOfPeriod'>;

export type Xref02ClinicalViolation = { field: Xref02ClinicalField; length: number; max: number };

/** Fields over their own XREF02 limit, in form order. Length is `value.length`, as the builder counts. */
export function xref02ClinicalViolations(input: Record<Xref02ClinicalField, string>): Xref02ClinicalViolation[] {
  const order: Xref02ClinicalField[] = ['anamnesis', 'actualMedicine', 'cave', 'examination'];
  return order
    .filter((field) => input[field].length > XREF02_FIELD_MAX[field])
    .map((field) => ({ field, length: input[field].length, max: XREF02_FIELD_MAX[field] }));
}

/** Anamnesis on XREF06 (speciallægehenvisning). The builder rejects a longer value. */
export const CLINICAL_INFO_MAX_TOTAL_XREF06 = 6_300;

/** ClinicalReason and Supplementary. */
export const TEXT_FIELD_MAX = 350;

export const DIAGNOSE_TEXT_MAX = 70;
export const DIAGNOSE_CODE_MAX = 17;
export const DIAGNOSE_TYPE_CODE = 'SKSdiagnosekode';
export const DIAGNOSE_DESCRIPTION_CODE = 'henv_diagnose';
export const ADDITIONAL_DIAGNOSE_BIDIAGNOSE = 'bidiagnose';
export const ADDITIONAL_DIAGNOSE_TILLAEGSDIAGNOSE = 'tillaegsdiagnose';

/**
 * DiagnoseDescriptionCode of a ReferralAdditional (XREF01/XREF02).
 *
 * Official DiagnoseDescriptionCodeType (2017/05/01 ValueLists.xsd) is the full
 * list in xref01Codes.ts. ReferralAdditional accepts every value except the
 * Refer-only henv_* codes (Testcenter: "Ukendt DiagnoseDescriptionCode:
 * henv_diagnose"). REF01 lets the clinician pick the type (default
 * tillaegsdiagnose). This helper is the XREF02 path: Refer henv_diagnose goes
 * with bidiagnose (XREF02_m_fraser_bilag). Refer henv_diagnose_ny_sygdom or
 * henv_diagnose_samme_sygdom (viderehenvisning) goes with tillaegsdiagnose
 * (XREF01_viderehenv_LPR).
 */
export function additionalDiagnoseDescriptionCode(referDiagnoseDescriptionCode: string): string {
  return referDiagnoseDescriptionCode === DIAGNOSE_DESCRIPTION_CODE
    ? ADDITIONAL_DIAGNOSE_BIDIAGNOSE
    : ADDITIONAL_DIAGNOSE_TILLAEGSDIAGNOSE;
}

export const WARNING_DIAGNOSIS = 'Der skal være en henvisningsdiagnose';
export const WARNING_ANAMNESIS = 'Der skal udfyldes noget i anamnesefeltet';
export const WARNING_ANDET = 'Andet skal udfyldes, når prioriteten er Se særlige forhold.';

export const STAMKORT_MAX = {
  surname: 70,
  givenName: 70,
  jobTitle: 35,
  street: 35,
  postCode: 9,
  district: 35,
  email: 70,
  telephone: 25,
} as const;

export const PARTY_MAX = {
  ean: 35,
  identifier: 17,
  organisationName: 35,
  street: 35,
  postCode: 9,
  district: 35,
  telephone: 25,
  referrerName: 35,
  referrerTitle: 35,
} as const;

export type StamkortField = keyof typeof STAMKORT_MAX;

export type StamkortLengthInput = {
  surname: string;
  givenName: string;
  jobTitle: string;
  street: string;
  postCode: string;
  district: string;
  email: string;
  telephones: readonly string[];
};

export type StamkortLengthViolation = {
  field: StamkortField;
  length: number;
  max: number;
};

export type ProtocolFocus = 'diagnosis' | 'andet' | 'anamnesis';

export type MedcomRelationCode = 'mor' | 'far' | 'barn' | 'uspec_paaroerende';

const PERSON_RELATION_SET: ReadonlySet<string> = new Set(PERSON_RELATION_TYPES);

function isPersonRelation(value: string): value is PersonRelationType {
  return PERSON_RELATION_SET.has(value);
}

/**
 * Century from the 7th CPR digit (CPR-kontoret):
 * 0–3 → 1900–1999
 * 4 or 9, year 00–36 → 2000–2036; year 37–99 → 1900–1999
 * 5–8, year 00–57 → 2000–2057; year 58–99 → 1800–1899
 */
export function cprCenturyBase(yearTwoDigits: number, seventhDigit: number): number | null {
  if (seventhDigit >= 0 && seventhDigit <= 3) return 1900;
  if (seventhDigit === 4 || seventhDigit === 9) {
    return yearTwoDigits <= 36 ? 2000 : 1900;
  }
  if (seventhDigit >= 5 && seventhDigit <= 8) {
    return yearTwoDigits <= 57 ? 2000 : 1800;
  }
  return null;
}

export function cprDigits(cpr: string): string | null {
  const digits = cpr.replace(/\D/g, '');
  return digits.length === 10 ? digits : null;
}

export type CprBirthDate = { year: number; month: number; day: number };

function isRealCalendarDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/** Birth date from a CPR, or null when the number does not encode a real date. */
export function birthDateFromCpr(cpr: string): CprBirthDate | null {
  const digits = cprDigits(cpr);
  if (!digits) return null;
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const yearTwo = Number(digits.slice(4, 6));
  const seventh = Number(digits[6]);
  const century = cprCenturyBase(yearTwo, seventh);
  if (century === null) return null;
  const year = century + yearTwo;
  if (!isRealCalendarDate(year, month, day)) return null;
  return { year, month, day };
}

/** Completed years on `today`, using the local calendar date. Null if the CPR has no birth date. */
export function ageFromCpr(cpr: string, today: Date): number | null {
  const birth = birthDateFromCpr(cpr);
  if (!birth) return null;
  let age = today.getFullYear() - birth.year;
  const month = today.getMonth() + 1;
  const day = today.getDate();
  if (month < birth.month || (month === birth.month && day < birth.day)) {
    age -= 1;
  }
  return age;
}

/**
 * Sex from the 10th CPR digit: odd = male, even = female
 * (borger.dk / Danmarks Statistik). Null when the CPR is not 10 digits.
 */
export function sexFromCpr(cpr: string): 'male' | 'female' | null {
  const digits = cprDigits(cpr);
  if (!digits) return null;
  const tenth = Number(digits[9]);
  return tenth % 2 === 1 ? 'male' : 'female';
}

export function isUnder15(cpr: string, today: Date): boolean {
  const age = ageFromCpr(cpr, today);
  return age !== null && age < 15;
}

/**
 * Provisional FirstDateOfPeriod wire strings for XREF02 pregnancy.
 * FormattedTextType is free text (no enumeration). MedCom's seven XREF02
 * examples only show "Ikke gravid."; the other three options are taken from
 * certification step 3.11 wording, with a trailing period to match that one
 * confirmed example. MedCom has been asked to confirm the exact strings and
 * has not answered yet — change this one constant when they do.
 */
export const XREF02_PREGNANCY_WIRE = {
  notRelevant: 'Ikke relevant.',
  notFilled: 'Ikke udfyldt.',
  pregnant: 'Gravid.',
  notPregnant: 'Ikke gravid.',
} as const;

export type Xref02PregnancyChoice = keyof typeof XREF02_PREGNANCY_WIRE;

export const WARNING_CLINICAL_REASON = 'Ønsket undersøgelse skal udfyldes';

export const MISSING_CLINICAL_REASON_CODE = 'missing-clinical-reason';
export const MISSING_CLINICAL_REASON_MESSAGE =
  'Henvisningen mangler ClinicalReason. Udfyld ønsket undersøgelse, behandling eller problemstilling.';

export function hasClinicalReason(value: string | readonly string[] | null | undefined): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  if (!value) return false;
  return value.some((line) => line.trim().length > 0);
}

export function clinicalReasonPayload(value: string): string[] | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? [trimmed] : null;
}

/**
 * Same total the builder uses: each field is `lines.join("").length`, then the
 * four totals are added. A single textarea is one line, so this is the sum of
 * the four strings. Break separators between fields are not counted.
 */
export function clinicalInfoCombinedLength(
  anamnesis: string,
  actualMedicine: string,
  cave: string,
  examination: string,
): number {
  return anamnesis.length + actualMedicine.length + cave.length + examination.length;
}

export function clinicalInfoOverLimit(
  anamnesis: string,
  actualMedicine: string,
  cave: string,
  examination: string,
  maxTotal: number = CLINICAL_INFO_MAX_TOTAL,
): boolean {
  return (
    clinicalInfoCombinedLength(anamnesis, actualMedicine, cave, examination) > maxTotal
  );
}

/** Index of the first code unit outside ISO-8859-1, matching the builder's charCodeAt > 255 check. */
export function firstNonLatin1(value: string): number | null {
  for (let i = 0; i < value.length; i++) {
    if (value.charCodeAt(i) > 255) return i;
  }
  return null;
}

export function isLatin1(value: string): boolean {
  return firstNonLatin1(value) === null;
}

export function stamkortLengthViolations(input: StamkortLengthInput): StamkortLengthViolation[] {
  const violations: StamkortLengthViolation[] = [];
  const check = (field: Exclude<StamkortField, 'telephone'>, value: string) => {
    const max = STAMKORT_MAX[field];
    if (value.length > max) violations.push({ field, length: value.length, max });
  };
  check('surname', input.surname);
  check('givenName', input.givenName);
  check('jobTitle', input.jobTitle);
  check('street', input.street);
  check('postCode', input.postCode);
  check('district', input.district);
  check('email', input.email);
  for (const telephone of input.telephones) {
    if (telephone.length > STAMKORT_MAX.telephone) {
      violations.push({
        field: 'telephone',
        length: telephone.length,
        max: STAMKORT_MAX.telephone,
      });
    }
  }
  return violations;
}

export function readPostalAddress(value: Json | null | undefined): {
  street: string;
  postCode: string;
  district: string;
} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { street: '', postCode: '', district: '' };
  }
  const record = value as Record<string, Json | undefined>;
  const street = typeof record.street === 'string' ? record.street : '';
  const postCode =
    typeof record.zip_code === 'string'
      ? record.zip_code
      : typeof record.zip === 'string'
        ? record.zip
        : '';
  const district = typeof record.city === 'string' ? record.city : '';
  return { street, postCode, district };
}

export type SenderContactGap = 'postcode' | 'phone';

/**
 * Optional on the schema: empty values are omitted from Sender, never invented.
 * MedCom's own XREF01 examples always include both, so the form can warn.
 */
export function senderContactGaps(postCode: string, telephone: string): SenderContactGap[] {
  const gaps: SenderContactGap[] = [];
  if (postCode.trim().length === 0) gaps.push('postcode');
  if (telephone.trim().length === 0) gaps.push('phone');
  return gaps;
}

export function diagnoseTextForReferral(textDa: string): string {
  return textDa.slice(0, DIAGNOSE_TEXT_MAX);
}

/** 32 hex characters. A hyphenated UUID is 36 and does not fit an..35. */
export function newEpisodeIdentifier(randomUuid: () => string = () => crypto.randomUUID()): string {
  return randomUuid().replace(/-/g, '');
}

export function medcomRelationCode(relationType: string): MedcomRelationCode | null {
  if (!isPersonRelation(relationType)) return null;
  switch (relationType) {
    case 'mother':
      return 'mor';
    case 'father':
      return 'far';
    case 'child':
    case 'son':
    case 'daughter':
      return 'barn';
    case 'parent':
    case 'husband':
    case 'wife':
    case 'spouse':
    case 'partner':
    case 'sister':
    case 'brother':
    case 'sibling':
    case 'grandmother':
    case 'grandfather':
    case 'grandparent':
    case 'grandchild':
    case 'guardian':
    case 'ward':
    case 'other':
      return 'uspec_paaroerende';
    default: {
      const exhaustive: never = relationType;
      return exhaustive;
    }
  }
}

export function hasMotherOrFatherRelation(relationTypes: readonly string[]): boolean {
  return relationTypes.some((type) => type === 'mother' || type === 'father');
}

/** Any person relation that maps to an official XREF01 RelationCode. */
export function hasSelectableReferralRelative(relationTypes: readonly string[]): boolean {
  return relationTypes.some((type) => medcomRelationCode(type) !== null);
}

export function chooseRelativeForXml<T extends { relationType: string }>(
  rows: readonly T[],
): T | null {
  const people = rows.filter((row) => medcomRelationCode(row.relationType) !== null);
  const mother = people.find((row) => row.relationType === 'mother');
  if (mother) return mother;
  const father = people.find((row) => row.relationType === 'father');
  if (father) return father;
  return people[0] ?? null;
}

export function under15BlocksReferral(
  cpr: string,
  relationTypes: readonly string[],
  today: Date,
): boolean {
  return isUnder15(cpr, today) && !hasSelectableReferralRelative(relationTypes);
}

export type ProtocolWarning =
  | typeof WARNING_DIAGNOSIS
  | typeof WARNING_ANDET
  | typeof WARNING_ANAMNESIS;

export function protocolWarnings(input: {
  hasPrimaryDiagnosis: boolean;
  priority: string;
  supplementary: string;
  anamnesis: string;
}): ProtocolWarning[] {
  const warnings: ProtocolWarning[] = [];
  if (!input.hasPrimaryDiagnosis) warnings.push(WARNING_DIAGNOSIS);
  if (input.priority === 'saerlige_forhold' && input.supplementary.trim().length === 0) {
    warnings.push(WARNING_ANDET);
  }
  if (input.anamnesis.trim().length === 0) warnings.push(WARNING_ANAMNESIS);
  return warnings;
}

export function protocolWarningFocus(warnings: readonly string[]): ProtocolFocus | null {
  if (warnings.includes(WARNING_DIAGNOSIS)) return 'diagnosis';
  if (warnings.includes(WARNING_ANDET)) return 'andet';
  if (warnings.includes(WARNING_ANAMNESIS)) return 'anamnesis';
  return null;
}

/** The only IdentifierCode values MedCom uses on XREF01. */
export const XREF01_IDENTIFIER_CODES = ['sygehusafdelingsnummer', 'ydernummer'] as const;

export type Xref01IdentifierCode = (typeof XREF01_IDENTIFIER_CODES)[number];

/**
 * The SOR picker also offers `lokationsnummer` where the message allows it (XDIS91 + XBIN01
 * korrespondance: IdentifierCodeType in 2006/07/01 and 2012/03/28 ValueLists.xsd). Never on XREF01.
 */
export type PickerIdentifierCode = Xref01IdentifierCode | 'lokationsnummer';

export function isPickerIdentifierCode(value: string | null | undefined): value is PickerIdentifierCode {
  return value === 'lokationsnummer' || isXref01IdentifierCode(value);
}

/**
 * Receiver Identifier/IdentifierCode for a korrespondance. The SOR register pair, or the
 * clinician's choice, when complete. Otherwise, and always for lokationsnummer, the EAN as
 * lokationsnummer, as before 17.10 (C10).
 */
export function correspondenceReceiverIdentity(input: {
  ean: string;
  registerCode: string | null;
  chosenCode: PickerIdentifierCode | null;
  identifier: string;
}): { identifier: string; identifierCode: PickerIdentifierCode } {
  const code = isXref01IdentifierCode(input.registerCode) ? input.registerCode : input.chosenCode;
  const identifier = input.identifier.trim();
  if (code && code !== 'lokationsnummer' && identifier.length > 0) return { identifier, identifierCode: code };
  return { identifier: input.ean, identifierCode: 'lokationsnummer' };
}

export type ReceiverIdentifierParty = {
  registerCode: string | null;
  chosenCode: string | null;
  identifier: string;
};

export type IdentifierPairGap = 'code' | 'identifier' | 'both';

export type IdentifierPairResult =
  | { ok: true; identifierCode: Xref01IdentifierCode; identifier: string }
  | { ok: false; missing: IdentifierPairGap };

export function isXref01IdentifierCode(value: string | null | undefined): value is Xref01IdentifierCode {
  return value === 'sygehusafdelingsnummer' || value === 'ydernummer';
}

/**
 * Register code wins when SOR supplied one. Otherwise the clinician's choice is used.
 * A blank identifier, or a code outside the two XREF01 values, is not a complete pair.
 */
export function receiverIdentifierPair(party: ReceiverIdentifierParty): IdentifierPairResult {
  const register = party.registerCode?.trim() ?? '';
  const chosen = party.chosenCode?.trim() ?? '';
  const identifierCode = isXref01IdentifierCode(register)
    ? register
    : isXref01IdentifierCode(chosen)
      ? chosen
      : null;
  const identifier = party.identifier.trim();
  if (identifierCode && identifier.length > 0) {
    return { ok: true, identifierCode, identifier: party.identifier };
  }
  if (!identifierCode && identifier.length === 0) return { ok: false, missing: 'both' };
  if (!identifierCode) return { ok: false, missing: 'code' };
  return { ok: false, missing: 'identifier' };
}

export type Xref01PartyRole = 'receiver' | 'copy';

export type Xref01IdentifierIssue = {
  role: Xref01PartyRole;
  missing: IdentifierPairGap;
};

/** No copy recipient is complete. A chosen copy recipient needs the same identifier pair. */
export function xref01IdentifierIssues(input: {
  receiver: ReceiverIdentifierParty;
  copy: ReceiverIdentifierParty | null;
}): Xref01IdentifierIssue[] {
  const issues: Xref01IdentifierIssue[] = [];
  const receiver = receiverIdentifierPair(input.receiver);
  if (!receiver.ok) issues.push({ role: 'receiver', missing: receiver.missing });
  if (input.copy) {
    const copy = receiverIdentifierPair(input.copy);
    if (!copy.ok) issues.push({ role: 'copy', missing: copy.missing });
  }
  return issues;
}

/** Reference: description an..70, URL an..350, at most 10. */
export const REFERENCE_DESCRIPTION_MAX = 70;
export const REFERENCE_URL_MAX = 350;
export const MAX_REFERENCES = 10;

export type UrlReferenceDraft = {
  description: string;
  url: string;
};

export type UrlReference = {
  kind: 'url';
  refDescription: string;
  url: string;
};

export type UrlReferenceIssue =
  | { index: number; code: 'incomplete' | 'latin1' | 'too_many' }
  | { index: number; code: 'description_long' | 'url_long'; length: number; max: number };

/**
 * Blank rows are left out. A row with only one of the two fields is refused.
 * The returned references are what the referral request sends.
 */
export function urlReferencesForRequest(
  rows: readonly UrlReferenceDraft[],
): { ok: true; references: UrlReference[] } | { ok: false; issues: UrlReferenceIssue[] } {
  const filled = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.description.trim().length > 0 || row.url.trim().length > 0);
  const issues: UrlReferenceIssue[] = [];
  if (filled.length > MAX_REFERENCES) {
    issues.push({ index: filled[MAX_REFERENCES].index, code: 'too_many' });
  }
  const references: UrlReference[] = [];
  for (const { row, index } of filled) {
    const description = row.description.trim();
    const url = row.url.trim();
    if (description.length === 0 || url.length === 0) {
      issues.push({ index, code: 'incomplete' });
      continue;
    }
    let rowOk = true;
    if (description.length > REFERENCE_DESCRIPTION_MAX) {
      rowOk = false;
      issues.push({
        index,
        code: 'description_long',
        length: description.length,
        max: REFERENCE_DESCRIPTION_MAX,
      });
    }
    if (url.length > REFERENCE_URL_MAX) {
      rowOk = false;
      issues.push({ index, code: 'url_long', length: url.length, max: REFERENCE_URL_MAX });
    }
    if (!isLatin1(description) || !isLatin1(url)) {
      rowOk = false;
      issues.push({ index, code: 'latin1' });
    }
    if (rowOk && filled.length <= MAX_REFERENCES) {
      references.push({ kind: 'url', refDescription: description, url });
    }
  }
  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, references };
}

/** Primary diagnosis qualifier when the referral forwards an existing LPR pathway. */
export const DIAGNOSE_DESCRIPTION_NY_SYGDOM = 'henv_diagnose_ny_sygdom';
export const DIAGNOSE_DESCRIPTION_SAMME_SYGDOM = 'henv_diagnose_samme_sygdom';

export type LprIllness = typeof DIAGNOSE_DESCRIPTION_NY_SYGDOM | typeof DIAGNOSE_DESCRIPTION_SAMME_SYGDOM;

/** The two free-choice codes on `lpr_sks_codes` list `admin.fritvalg`. */
export const LPR_HOSPITAL_CHOICE_CODES = ['ALDB01', 'ALDB02'] as const;

export type LprHospitalChoiceCode = (typeof LPR_HOSPITAL_CHOICE_CODES)[number];

export function isLprHospitalChoiceCode(value: string | null | undefined): value is LprHospitalChoiceCode {
  return value === 'ALDB01' || value === 'ALDB02';
}

export function isLprIllness(value: string | null | undefined): value is LprIllness {
  return value === DIAGNOSE_DESCRIPTION_NY_SYGDOM || value === DIAGNOSE_DESCRIPTION_SAMME_SYGDOM;
}

/**
 * `lpr_forloeb.object_id` is a hyphenated UUID (36). EpisodeOfCareIdentifier is an..35,
 * so the hyphens are removed. 32 hex characters fit.
 */
export function lprEpisodeIdentifier(objectId: string): string | null {
  const identifier = objectId.trim().replace(/-/g, '');
  if (identifier.length === 0 || identifier.length > 35) return null;
  return identifier;
}

export type Xref01EpisodeRequest =
  | { type: 'henviser'; identifier: string }
  | { type: 'LPR'; identifier: string };

export type ReferralLprFields = {
  episodesOfCare: Xref01EpisodeRequest[];
  diagnoseDescriptionCode: string;
  hospitalChoice?: LprHospitalChoiceCode;
};

export type ReferralLprReason = 'missing_illness' | 'missing_object' | 'bad_identifier';

/**
 * When `forward` is false the LPR section is hidden: one referring episode,
 * `henv_diagnose`, and no hospital choice. When it is true, the second episode,
 * the illness qualifier and an optional ALDB01/ALDB02 are the request fields.
 */
export function referralLprFields(input: {
  referringIdentifier: string;
  forward: boolean;
  objectId: string | null;
  illness: string | null;
  hospitalChoice: string | null;
}): { ok: true; fields: ReferralLprFields } | { ok: false; reason: ReferralLprReason } {
  const referring: Xref01EpisodeRequest = { type: 'henviser', identifier: input.referringIdentifier };
  if (!input.forward) {
    return {
      ok: true,
      fields: {
        episodesOfCare: [referring],
        diagnoseDescriptionCode: DIAGNOSE_DESCRIPTION_CODE,
      },
    };
  }
  if (!isLprIllness(input.illness)) return { ok: false, reason: 'missing_illness' };
  if (!input.objectId) return { ok: false, reason: 'missing_object' };
  const identifier = lprEpisodeIdentifier(input.objectId);
  if (!identifier) return { ok: false, reason: 'bad_identifier' };
  const hospitalChoice = isLprHospitalChoiceCode(input.hospitalChoice) ? input.hospitalChoice : undefined;
  return {
    ok: true,
    fields: {
      episodesOfCare: [referring, { type: 'LPR', identifier }],
      diagnoseDescriptionCode: input.illness,
      ...(hospitalChoice ? { hospitalChoice } : {}),
    },
  };
}
